import { toMoment, formatDate } from '@shared/utils/date';
import {
	DATE_AND_TIME_DMY,
	TimeZoneFormat,
} from '@shared/utils/constants';

export type HirePeriodInterval = {
	id: number;
	from: string;
	to: string;
	label?: string;
};

export type HirePeriodConflict =
	{ type: 'invalidRange'; period: HirePeriodInterval } |
	{ type: 'overlap'; period: HirePeriodInterval; conflictsWith: HirePeriodInterval };

const formatPeriodRange = (from: string, to: string) => {
	const fromLabel = formatDate(toMoment(from), DATE_AND_TIME_DMY, true, TimeZoneFormat.UTC);
	const toLabel = formatDate(toMoment(to), DATE_AND_TIME_DMY, true, TimeZoneFormat.UTC);

	return `${fromLabel} - ${toLabel}`;
};

export const formatHirePeriodConflictMessage = (conflict: HirePeriodConflict): string => {
	if (conflict.type === 'invalidRange') {
		return 'Hire period "to" must be after "from"';
	}

	const periodLabel = conflict.period.label ??
		formatPeriodRange(conflict.period.from, conflict.period.to);
	const otherLabel = conflict.conflictsWith.label ??
		formatPeriodRange(conflict.conflictsWith.from, conflict.conflictsWith.to);

	return `Hire period ${periodLabel} overlaps ${otherLabel}`;
};

/**
 * Finds the first invalid or overlapping hire period among `periods`.
 * Intervals use half-open [from, to) semantics so back-to-back periods
 * (p1.to === p2.from) are legal.
 */
const findHirePeriodOverlap = (periods: HirePeriodInterval[]): HirePeriodConflict | null => {
	for (let i = 0; i < periods.length; i++) {
		const period = periods[i];

		if (toMoment(period.to).isSameOrBefore(toMoment(period.from))) {
			return { type: 'invalidRange', period };
		}
	}

	for (let i = 0; i < periods.length; i++) {
		const a = periods[i];

		for (let j = i + 1; j < periods.length; j++) {
			const b = periods[j];

			const overlaps =
				toMoment(a.from).isBefore(toMoment(b.to)) &&
				toMoment(a.to).isAfter(toMoment(b.from));

			if (overlaps) {
				return { type: 'overlap', period: a, conflictsWith: b };
			}
		}
	}

	return null;
};

export default findHirePeriodOverlap;
